import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, UserContext } from '@/App';
import { ArrowLeft, Package, Clock, CheckCircle, Truck } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { user, login } = useContext(UserContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { login(); return; }
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders/my`, { withCredentials: true });
      setOrders(res.data);
    } catch (error) {
      if (error.response?.status === 401) {
        login();
      } else {
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = { pending: Clock, confirmed: Package, preparing: Package, out_for_delivery: Truck, delivered: CheckCircle };
    const Icon = icons[status] || Package;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full"><ArrowLeft className="w-6 h-6" /></button>
            <h1 className="text-2xl font-bold">My Orders</h1>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div></div> : orders.length === 0 ? (
          <div className="text-center py-20"><Package className="w-20 h-20 mx-auto text-gray-300 mb-4" /><h2 className="text-xl font-semibold text-gray-600 mb-2">No orders yet</h2><button onClick={() => navigate('/')} className="btn-primary text-white px-8 py-3 rounded-full">Start Ordering</button></div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.id} className="bg-white rounded-xl shadow-md p-6">
                <div className="flex justify-between mb-4">
                  <div><h3 className="font-bold text-lg">Order #{order.id.slice(0, 8)}</h3><p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p></div>
                  <div className={`order-status-badge status-${order.status} flex items-center gap-1`}>{getStatusIcon(order.status)} {order.status.replace('_', ' ')}</div>
                </div>
                <div className="space-y-2 mb-4">{order.items.map((item, idx) => <div key={idx} className="flex justify-between text-sm"><span>{item.name} x {item.quantity}</span><span className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</span></div>)}</div>
                <div className="border-t pt-4 flex justify-between items-center">
                  <div><p className="text-sm text-gray-600">Total Amount</p><p className="text-xl font-bold text-blue-600">₹{order.total_amount.toFixed(2)}</p></div>
                  <div className="flex gap-3">
                    <button onClick={() => navigate(`/track/${order.id}`)} className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg font-semibold hover:bg-blue-50">Track Order</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default OrdersPage;
